import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  type DataSelectableWithChildren,
  IpponBadge,
  IpponButton,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponIcon,
  IpponIconTile,
  IpponImportFile,
  IpponText,
  IpponTitle,
  IpponVSpace,
} from '@ippon-ui/react';
import type { IconClassic } from '@ippon-ui/icons';
import {
  InvalidMimeTypeError,
  RulesFileToImport,
} from '@/rules-management/domain/RulesFileToImport.ts';
import { Optional, Result } from '@/generic/functional';
import { useDialog } from '@/generic/react-tools';
import { type Page, Pagination } from '@/generic/pagination';
import { inject } from '@/rules-management/injections.ts';
import { RULES_REPOSITORY } from '@/rules-management/RulesManagementKeys.ts';
import type { DraftRule } from '@/rules-management/domain/DraftRule.ts';
import {
  type ExtractionFailure,
  ExtractionStep,
} from '@/rules-management/primary/ExtractionFailure.ts';
import { RulesExtractionLoadingModalComponent } from '@/rules-management/primary/RulesExtractionLoadingModalComponent.tsx';
import { DraftRulesPublicationComponent } from '@/rules-management/primary/DraftRulesPublicationComponent.tsx';
import { useDraftRulesPublication } from '@/rules-management/primary/UseDraftRulesPublication.ts';
import { useDraftRulesEdition } from '@/rules-management/primary/UseDraftRulesEdition.ts';
import { useRuleExtractionTracking } from '@/rules-management/primary/RuleExtractionTrackingContext.ts';

const ListItem = ({
  icon,
  children,
  dataSelector,
}: DataSelectableWithChildren<{
  icon: IconClassic;
}>) => (
  <IpponHSpace gap={4} align="middle" tag="li">
    <IpponIcon name={icon} variant="outline" size={16} color="brand-primary" />
    <IpponText variant="body" dataSelector={dataSelector}>
      {children}
    </IpponText>
  </IpponHSpace>
);

const HowItWorksSection = () => {
  const { t } = useTranslation('rules-management');
  return (
    <IpponVSpace gap={8}>
      <IpponText
        variant="body"
        size="large"
        weight="bold"
        tag="h3"
        dataSelector="rules-management.rules.import.how-it-works.title"
      >
        {t('rules-management.rules.import.how-it-works.title')}
      </IpponText>
      <IpponText
        variant="body"
        color="neutral-tertiary-inversed"
        tag="p"
        dataSelector="rules-management.rules.import.how-it-works.description"
      >
        {t('rules-management.rules.import.how-it-works.description')}
      </IpponText>
      <IpponVSpace tag="ul">
        <ListItem dataSelector="rules-management.rules.import.how-it-works.faster" icon="flash">
          {t('rules-management.rules.import.how-it-works.faster')}
        </ListItem>
        <ListItem dataSelector="rules-management.rules.import.how-it-works.precision" icon="locate">
          {t('rules-management.rules.import.how-it-works.precision')}
        </ListItem>
        <ListItem
          icon="document-text"
          dataSelector="rules-management.rules.import.how-it-works.report"
        >
          {t('rules-management.rules.import.how-it-works.report')}
        </ListItem>
      </IpponVSpace>
    </IpponVSpace>
  );
};

const PracticalTipsSection = () => {
  const { t } = useTranslation('rules-management');
  return (
    <IpponVSpace gap={8}>
      <IpponText
        variant="body"
        size="large"
        weight="bold"
        tag="h3"
        dataSelector="rules-management.rules.import.practical-tips.title"
      >
        {t('rules-management.rules.import.practical-tips.title')}
      </IpponText>
      <IpponText
        variant="body"
        color="neutral-tertiary-inversed"
        dataSelector="rules-management.rules.import.practical-tips.description"
        tag="p"
      >
        {t('rules-management.rules.import.practical-tips.description')}
      </IpponText>
      <IpponVSpace tag="ul">
        <ListItem
          icon="document-text"
          dataSelector="rules-management.rules.import.practical-tips.structure"
        >
          {t('rules-management.rules.import.practical-tips.structure')}
        </ListItem>
        <ListItem
          icon="locate"
          dataSelector="rules-management.rules.import.practical-tips.specific"
        >
          {t('rules-management.rules.import.practical-tips.specific')}
        </ListItem>
        <ListItem icon="search" dataSelector="rules-management.rules.import.practical-tips.review">
          {t('rules-management.rules.import.practical-tips.review')}
        </ListItem>
        <ListItem
          icon="checkbox"
          dataSelector="rules-management.rules.import.practical-tips.activate"
        >
          {t('rules-management.rules.import.practical-tips.activate')}
        </ListItem>
      </IpponVSpace>
    </IpponVSpace>
  );
};

export const InvalidFileFormatError = ({ error }: { error: InvalidMimeTypeError }) => {
  const { t } = useTranslation('rules-management');

  const message = t('rules-management.rules.import.error.invalid-format', {
    mime: error.mime,
    interpolation: { escapeValue: false },
  });

  return (
    <IpponText
      variant="body"
      color="error-primary"
      dataSelector="rules-management.rules.import.error"
    >
      {message}
    </IpponText>
  );
};

const FileImportResult = ({
  fileResult,
  onRemove,
}: {
  fileResult: Optional<Result<InvalidMimeTypeError, RulesFileToImport>>;
  onRemove: () => void;
}) => {
  return fileResult
    .map((result) =>
      result.fold({
        error: (error) => (
          <InvalidFileFormatError key="rules-management.rules.import.error" error={error} />
        ),
        success: (file) => (
          <IpponBadge
            color="neutral"
            variant="secondary"
            dataSelector="rules-management.rules.import.file-name"
            iconRight={{
              name: 'close',
              variant: 'outline',
              onClick: onRemove,
              dataSelector: 'rules-management.rules.import.file-name.remove',
            }}
          >
            {file.file.name}
          </IpponBadge>
        ),
      }),
    )
    .orUndefined();
};

const ExtractButton = ({ onExtract }: { onExtract: () => Promise<void> }) => {
  const { t } = useTranslation('rules-management');
  return (
    <IpponButton onClick={onExtract} dataSelector="rules-management.rules.import.extract">
      {t('rules-management.rules.import.extract')}
    </IpponButton>
  );
};

interface ImportContentProps {
  fileResult: Optional<Result<InvalidMimeTypeError, RulesFileToImport>>;
  onFileChange: (file: File) => void;
  onRemove: () => void;
}

const ImportContent = ({ fileResult, onFileChange, onRemove }: ImportContentProps) => {
  const { t } = useTranslation('rules-management');

  return (
    <IpponVSpace gap={16}>
      <IpponImportFile
        title={t('rules-management.rules.import.file-input.title')}
        description={t('rules-management.rules.import.file-input.description')}
        dataSelector="rules-management.rules.import.file-input"
        onChange={onFileChange}
      />
      <IpponHSpace gap={4} align="middle">
        <FileImportResult fileResult={fileResult} onRemove={onRemove} />
      </IpponHSpace>
    </IpponVSpace>
  );
};

const ImportView = ({
  fileResult,
  onFileChange,
  onRemove,
  validFile,
  onExtract,
}: {
  fileResult: Optional<Result<InvalidMimeTypeError, RulesFileToImport>>;
  onFileChange: (file: File) => void;
  onRemove: () => void;
  validFile: Optional<RulesFileToImport>;
  onExtract: () => Promise<void>;
}) => (
  <>
    <hr className="ippon-modal--separator" />
    <div className="ippon-modal--body">
      <IpponVSpace gap={32}>
        <ImportContent fileResult={fileResult} onFileChange={onFileChange} onRemove={onRemove} />
        <HowItWorksSection />
        <PracticalTipsSection />
      </IpponVSpace>
    </div>
    <div className="ippon-modal--footer">
      {validFile.isPresent() && <ExtractButton onExtract={onExtract} />}
    </div>
  </>
);

const DRAFT_RULES_PAGE_SIZE = 30;
const FIRST_DRAFT_RULES_PAGE = 1;

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export const RulesImportComponent = () => {
  const { t } = useTranslation('rules-management');
  const tracking = useRuleExtractionTracking();
  const [fileResult, setFileResult] = useState<
    Optional<Result<InvalidMimeTypeError, RulesFileToImport>>
  >(Optional.empty());
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [drafts, setDrafts] = useState<Optional<Page<DraftRule>>>(Optional.empty());
  const [loadFailure, setLoadFailure] = useState<Optional<ExtractionFailure>>(Optional.empty());

  const modalFailure: Optional<ExtractionFailure> = loadFailure.or(() => tracking.failure);

  const validFile: Optional<RulesFileToImport> = fileResult.flatMap((result) =>
    result.fold({
      error: () => Optional.empty(),
      success: (file) => Optional.of(file),
    }),
  );

  const handleFileChange = (file: File) => {
    const result = RulesFileToImport.of(file);
    setFileResult(Optional.of(result));
  };

  const handleRemove = () => {
    setFileResult(Optional.empty());
  };

  const loadDrafts = async (page: number): Promise<void> => {
    try {
      const loaded = await inject(RULES_REPOSITORY).listDrafts(
        Pagination.of({ page, pageSize: DRAFT_RULES_PAGE_SIZE }),
      );
      setDrafts(Optional.of(loaded));
    } catch (error) {
      setLoadFailure(Optional.of({ error: toError(error), step: ExtractionStep.LOADING_DRAFTS }));
    }
  };

  useEffect(() => {
    if (drafts.isEmpty()) {
      tracking.drafts.ifPresent((initial) => setDrafts(Optional.of(initial)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking.drafts]);

  const handleExtract = (): Promise<void> =>
    validFile.map((file) => tracking.extract(file)).orElseGet(() => Promise.resolve());

  const reloadDrafts = async (page: number): Promise<void> => {
    setLoadingDrafts(true);
    setLoadFailure(Optional.empty());
    try {
      await loadDrafts(page);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleRetry = () => {
    if (loadFailure.isPresent()) {
      void reloadDrafts(FIRST_DRAFT_RULES_PAGE);
      return;
    }
    tracking.retry();
  };

  const handleFailureClose = () => {
    if (loadFailure.isPresent()) {
      setLoadFailure(Optional.empty());
      return;
    }
    tracking.dismissFailure();
  };

  const handleClose = () => {
    setFileResult(Optional.empty());
    setDrafts(Optional.empty());
    setLoadFailure(Optional.empty());
    resetPublication();
    resetEdition();
    tracking.closeImport();
  };

  const {
    selection,
    setSelection,
    publicationFailure,
    handlePublish,
    discardFailure,
    handleDiscard,
    reset: resetPublication,
  } = useDraftRulesPublication({
    reloadDrafts: loadDrafts,
    onFullyPublished: handleClose,
  });

  const {
    editFailure,
    handleSave,
    reset: resetEdition,
  } = useDraftRulesEdition({
    reloadDrafts: loadDrafts,
  });

  const dialog = useDialog(tracking.visible, handleClose);

  const isResultView = drafts.isPresent();

  return (
    <>
      <dialog
        className="ippon-modal"
        id="rules-import"
        data-selector="rules-management.rules.import"
        ref={dialog}
      >
        <div className="ippon-modal--header">
          <IpponHSpace align="middle" gap={16}>
            <IpponIconTile icon={{ name: 'download', variant: 'outline' }} size="small" />
            <IpponHSpaceSlot expand={true}>
              <IpponVSpace>
                <hgroup>
                  <IpponTitle tag="h2" level={5} dataSelector="rules-management.rules.import.title">
                    {t('rules-management.rules.import.title')}
                  </IpponTitle>
                  <IpponText
                    variant="body"
                    color="neutral-tertiary-inversed"
                    tag="p"
                    dataSelector="rules-management.rules.import.subtitle"
                  >
                    {t('rules-management.rules.import.subtitle')}
                  </IpponText>
                </hgroup>
              </IpponVSpace>
            </IpponHSpaceSlot>
            <IpponButton
              color="neutral"
              variant="text"
              onClick={handleClose}
              iconLeft={{ name: 'close' }}
              dataSelector="rules-management.rules.import.close"
            />
          </IpponHSpace>
        </div>
        {isResultView ? (
          <DraftRulesPublicationComponent
            drafts={drafts.orElseThrow()}
            selection={selection}
            publicationFailure={publicationFailure}
            discardFailure={discardFailure}
            editFailure={editFailure}
            onSelectionChange={setSelection}
            onPageChange={(page) => void reloadDrafts(page)}
            onPublish={handlePublish}
            onDiscard={(draft, page) => void handleDiscard(draft, page)}
            onSave={(draft, edit, page) => handleSave(draft.id, edit, page)}
            onClose={handleClose}
          />
        ) : (
          <ImportView
            fileResult={fileResult}
            onFileChange={handleFileChange}
            onRemove={handleRemove}
            validFile={validFile}
            onExtract={handleExtract}
          />
        )}
      </dialog>
      <RulesExtractionLoadingModalComponent
        open={
          tracking.visible && (tracking.extracting || loadingDrafts || modalFailure.isPresent())
        }
        failure={modalFailure}
        onRetry={handleRetry}
        onClose={handleFailureClose}
        onContinueInBackground={tracking.continueInBackground}
      />
    </>
  );
};
